import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/styles/tailwind-utils';
import { AccountLayout } from '@components/layout/AccountLayout';

const SETTINGS_NAV = [
  { key: 'businessDetails',  href: '/account/settings/business-details' },
  { key: 'branding',         href: '/account/settings/branding' },
  { key: 'businessHours',    href: '/account/settings/business-hours' },
  { key: 'holidays',         href: '/account/settings/holidays' },
  { key: 'scheduleSettings', href: '/account/settings/schedule' },
  { key: 'chartTemplates',   href: '/account/settings/chart-templates' },
] as const;

interface BusinessSettingsLayoutProps {
  children: React.ReactNode;
}

export function BusinessSettingsLayout({ children }: BusinessSettingsLayoutProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <AccountLayout>
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6 md:py-8">
        <h1 className="mb-5 font-display text-2xl font-semibold text-foreground md:mb-6 md:text-3xl">
          {t('settings.title')}
        </h1>

        {/* Tab bar — scrolls horizontally on narrow screens */}
        <div className="border-b border-border">
          <nav
            className="-mb-px flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label={t('settings.title')}
          >
            {SETTINGS_NAV.map(({ key, href }) => {
              const isActive = pathname === href;
              return (
                <Link
                  key={href}
                  to={href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'shrink-0 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors md:px-4',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                  )}
                >
                  {t(`settings.nav.${key}`)}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="mt-6 md:mt-8">{children}</div>
      </div>
    </AccountLayout>
  );
}
